import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';


const UserForm = ({ onSubmit, editingUser, onCancel, roles = [], users = [], companies = [], formSuccess = '', formError = '', setFormSuccess, setFormError, fieldErrors = {}, setFieldErrors, resetKey }) => {
  const [form, setForm] = useState({ username: '', password: '', role_id: '', company_code: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editingUser) setForm(editingUser);
    else setForm({ username: '', password: '', role_id: '', company_code: '' });
    setErrors({});
    if (setFormSuccess) setFormSuccess('');
    if (setFormError) setFormError('');
  }, [editingUser, resetKey]);

  const validate = () => {
    const newErrors = {};
    if (!form.username.trim()) newErrors.username = 'Please enter a username.';
    if (!editingUser && !form.password.trim()) newErrors.password = 'Please enter a password.';
    if (!form.role_id) newErrors.role_id = 'Please select a role.';
    if (!form.company_code) newErrors.company_code = 'Please select a company.';
    setErrors(newErrors);
    if (setFormError) setFormError('');
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: undefined });
    if (setFormError) setFormError('');
    if (setFormSuccess) setFormSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!validate()) return;
      // Check for duplicate username (ignore case, ignore self if editing)
      const usernameExists = users.some(u => u.username.toLowerCase() === form.username.trim().toLowerCase() && (!editingUser || u.user_id !== editingUser.user_id));
      if (usernameExists) {
        setErrors({ ...errors, username: 'Username already exists.' });
        if (setFormError) setFormError('Username already exists.');
        return;
      }
      onSubmit(form);
      if (!editingUser) setForm({ username: '', password: '', role_id: '', company_code: '' });
      setErrors({});
      if (setFormSuccess) setFormSuccess(editingUser ? 'User updated successfully!' : 'User added successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setForm({ username: '', password: '', role_id: '', company_code: '' });
    setErrors({});
    if (setFormSuccess) setFormSuccess('');
    if (setFormError) setFormError('');
  };

  // Helper to get user display name and role
  const getUserDisplay = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => String(u.user_id) === String(userId));
    if (!user) return 'Unknown';
    return `${user.username} (${user.role_name || 'Unknown'})`;
  };

  // Helper to format date
  const formatDate = (date) => date ? dayjs(date).format('DD-MM-YYYY hh:mm A') : 'Unknown';

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 400, width: '100%', mx: 'auto', mt: 1, p: 0 }} autoComplete="off">
      <Box sx={{ fontWeight: 700, fontSize: 18, color: '#111', mb: 2.5, letterSpacing: 0.7, textAlign: 'left', width: '100%' }}>
        {editingUser ? 'Edit User' : 'Add User'}
      </Box>
      <Stack spacing={2} alignItems="center">
        <TextField
          label="Username*"
          name="username"
          value={form.username}
          onChange={handleChange}
          error={!!errors.username}
          helperText={errors.username}
          fullWidth
          inputProps={{ maxLength: 50 }}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#111',
              minHeight: 45,
              height: 45,
              '& fieldset': { borderColor: !!errors.username ? '#D32F2F' : '#BDBDBD' },
              '&:hover fieldset': { borderColor: !!errors.username ? '#D32F2F' : '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: !!errors.username ? '#D32F2F' : '#00C853' },
            },
            mb: 1.5,
            '& label.Mui-focused': { color: '#00C853' },
          }}
          InputProps={{
            sx: {
              background: '#fff',
              boxShadow: 'none',
              padding: 0,
              minHeight: 45,
              height: 45,
              '& input': {
                background: '#fff !important',
                boxShadow: 'none !important',
                padding: '0 8px',
                minHeight: 45,
                height: 45,
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitBackgroundClip: 'text',
              },
            },
          }}
        />
        <TextField
          label="Password*"
          name="password"
          type="password"
          value={form.password}
          onChange={handleChange}
          error={!!errors.password}
          helperText={errors.password}
          fullWidth
          inputProps={{ maxLength: 50 }}
          size="small"
          disabled={!!editingUser}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#111',
              minHeight: 45,
              height: 45,
              '& fieldset': { borderColor: !!errors.password ? '#D32F2F' : '#BDBDBD' },
              '&:hover fieldset': { borderColor: !!errors.password ? '#D32F2F' : '#BDBDBD' },
              '&.Mui-focused fieldset': { borderColor: !!errors.password ? '#D32F2F' : '#00C853' },
            },
            mb: 1.5,
            '& label.Mui-focused': { color: '#00C853' },
          }}
          InputProps={{
            sx: {
              background: '#fff',
              boxShadow: 'none',
              padding: 0,
              minHeight: 45,
              height: 45,
              '& input': {
                background: '#fff !important',
                boxShadow: 'none !important',
                padding: '0 8px',
                minHeight: 45,
                height: 45,
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitBackgroundClip: 'text',
              },
            },
          }}
        />
        <FormControl fullWidth error={!!errors.role_id} size="small">
          {/* Autocomplete for role: search by role name */}
          <Autocomplete
            id="role_id"
            options={roles}
            getOptionLabel={(option) => option.role_name || ''}
            value={roles.find(r => r.role_id === form.role_id) || null}
            onChange={(event, newValue) => {
              setForm({ ...form, role_id: newValue ? newValue.role_id : '' });
              setErrors(prev => ({ ...prev, role_id: undefined }));
              if (setFormError) setFormError('');
              if (setFormSuccess) setFormSuccess('');
            }}
            isOptionEqualToValue={(option, value) => option.role_id === value.role_id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Role"
                error={!!errors.role_id}
                helperText={errors.role_id}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: 45,
                    height: 45,
                    color: '#111',
                    '& fieldset': { borderColor: !!errors.role_id ? '#D32F2F' : '#BDBDBD' },
                    '&:hover fieldset': { borderColor: !!errors.role_id ? '#D32F2F' : '#BDBDBD' },
                    '&.Mui-focused fieldset': { borderColor: !!errors.role_id ? '#D32F2F' : '#00C853' },
                  },
                  '& label.Mui-focused': { color: '#00C853' },
                }}
                inputProps={{ ...params.inputProps, maxLength: 50 }}
              />
            )}
            filterOptions={(options, { inputValue }) =>
              options.filter(option =>
                option.role_name &&
                option.role_name.toLowerCase().includes(inputValue.toLowerCase())
              )
            }
            clearOnEscape
          />
        </FormControl>
        <FormControl fullWidth error={!!errors.company_code || !!fieldErrors.company_code} size="small">
          {/* Autocomplete for company: search by code or name, label as CMP001 _-Mindtree */}
          <Autocomplete
            id="company_code"
            options={companies}
            getOptionLabel={(option) => option.company_code && option.company_name ? `${option.company_code} - ${option.company_name}` : ''}
            value={companies.find(c => c.company_code === form.company_code) || null}
            onChange={(event, newValue) => {
              setForm({ ...form, company_code: newValue ? newValue.company_code : '' });
              setErrors(prev => ({ ...prev, company_code: undefined }));
              if (setFormError) setFormError('');
              if (setFormSuccess) setFormSuccess('');
            }}
            isOptionEqualToValue={(option, value) => option.company_code === value.company_code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Company Code"
                error={!!errors.company_code || !!fieldErrors.company_code}
                helperText={errors.company_code || fieldErrors.company_code}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: 45,
                    height: 45,
                    color: '#111',
                    '& fieldset': { borderColor: !!errors.company_code || !!fieldErrors.company_code ? '#D32F2F' : '#BDBDBD' },
                    '&:hover fieldset': { borderColor: !!errors.company_code || !!fieldErrors.company_code ? '#D32F2F' : '#BDBDBD' },
                    '&.Mui-focused fieldset': { borderColor: !!errors.company_code || !!fieldErrors.company_code ? '#D32F2F' : '#00C853' },
                  },
                  '& label.Mui-focused': { color: '#00C853' },
                }}
                inputProps={{ ...params.inputProps, maxLength: 50 }}
              />
            )}
            filterOptions={(options, { inputValue }) =>
              options.filter(option => {
                const search = inputValue.toLowerCase();
                return (
                  (option.company_name && option.company_name.toLowerCase().includes(search)) ||
                  (option.company_code && option.company_code.toLowerCase().includes(search))
                );
              })
            }
            clearOnEscape
          />
        </FormControl>
        {(editingUser || form.created_by || form.created_at) && (
          <Box sx={{ mt: 2, mb: 1, background: '#f8f9f6', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', p: 2, color: '#222', fontSize: 14, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#4caf50', marginRight: 6 }}> ●</span>
              <span style={{ fontWeight: 600, marginRight: 6 }}>Created by:</span>
              <span style={{ marginRight: 12 }}>{getUserDisplay(editingUser?.created_by || form.created_by)}</span>
              <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}> ⏰</span>
              <span>{formatDate(editingUser?.created_at || form.created_at)}</span>
            </div>
            {editingUser && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#ff9800', marginRight: 6 }}> ●</span>
                <span style={{ fontWeight: 600, marginRight: 6 }}>Last updated by:</span>
                <span style={{ marginRight: 12 }}>{getUserDisplay(editingUser.updated_by)}</span>
                <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}> ⏰</span>
                <span>{formatDate(editingUser.updated_at)}</span>
              </div>
            )}
          </Box>
        )}
        {formError && <Box sx={{ color: '#D32F2F', fontWeight: 500, fontSize: 15 }}>{formError}</Box>}
        {formSuccess && <Box sx={{ color: '#00C853', fontWeight: 500, fontSize: 15 }}>{formSuccess}</Box>}
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button type="submit" fullWidth variant="contained" sx={{ bgcolor: '#00C853', '&:hover': { bgcolor: '#009e46' }, minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1 }} disabled={isSubmitting}>
            {editingUser ? 'Update' : 'Add'}
          </Button>
          <Button type="button" fullWidth variant="outlined" sx={{ color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1, '&:hover, &:active': { borderColor: '#009e46', color: '#009e46' } }} onClick={handleClear} disabled={isSubmitting}>
            Clear
          </Button>
          {editingUser && (
            <Button type="button" fullWidth variant="outlined" color="#00C853" onClick={onCancel} disabled={isSubmitting} sx={{color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1 ,'&:hover, &:active': { borderColor: '#009e46', color: '#009e46' }}}>
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default UserForm;
